import { ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Link, Modal, ModalBody, ModalFooter, ModalHeader, Text, cn } from '@growtherapy/sprout-ui';

import { HelpModalItem } from '../components/help-modal/HelpModalItem';
import { CLIENT_TELEHEALTH_HELP_ARTICLE } from '../components/help-modal/constants';
import { Translate } from '../i18n/Translate';
import { Appointment } from '../types';
import { ProviderNotHereDescriptionWrapper as ProviderNotHereDescription } from './ProviderNotHereDescriptionWrapper';

export type HelpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isDarkTheme: boolean;
  contactUsUrl: string;
  appointment?: Pick<Appointment, 'hasProviderNoShowReport'> | null;
  audioVideoTroubleshootActionItem?: ReactNode;
  shouldShowProviderNoShowSection?: boolean;
  isPastNoShowThreshold: boolean;
  clientJoinedAfterWindowToReport: boolean;
  recordProviderNoShowAndRedirect: () => void;
};

export function HelpModal({
  isOpen,
  onClose,
  isDarkTheme,
  contactUsUrl,
  appointment,
  audioVideoTroubleshootActionItem: AudioVideoTroubleshootActionItem,
  shouldShowProviderNoShowSection,
  isPastNoShowThreshold,
  clientJoinedAfterWindowToReport,
  recordProviderNoShowAndRedirect,
}: HelpModalProps) {
  const t = useTranslate();

  const supportText = (
    <Text
      variant='sm'
      className={cn({
        'rebrand:text-text-inverse': isDarkTheme,
      })}
    >
      <Translate
        i18nKey='telehealth-core:troubleshooting.ifYouNeedAdditionalSupportReachOutToUs'
        components={{
          1: (
            <Link
              href={contactUsUrl}
              className={cn('text-neutral-700 hover:text-neutral-900', {
                'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                  isDarkTheme,
              })}
              size='sm'
              target='_blank'
              external={true}
            />
          ),
        }}
      />
    </Text>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={cn('rebrand:rounded-none', {
        'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
      })}
    >
      <ModalHeader
        className={cn({
          '[&_button]:rebrand:mobile:text-text-inverse': isDarkTheme,
        })}
        contentClassName={cn({
          'rebrand:text-text-inverse ': isDarkTheme,
        })}
      >
        {t('telehealth-core:troubleshooting.needHelp')}
      </ModalHeader>
      <ModalBody
        className={cn('sm:py-8 flex flex-col gap-6', {
          // Hands the native scroll UI over to the browser's dark rendering so its
          // track stops sitting on a light background inside the dark modal.
          'rebrand:[color-scheme:dark]': isDarkTheme,
        })}
      >
        <Text
          className={cn('text-neutral-800 rebrand:text-text-default', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
          as='h2'
        >
          {t('telehealth-core:troubleshooting.commonIssues')}
        </Text>
        <ol className='list-style-none flex flex-col gap-6'>
          {AudioVideoTroubleshootActionItem}
          {shouldShowProviderNoShowSection && (
            <HelpModalItem
              heading={t('telehealth-core:troubleshooting.mySessionHasntStarted')}
              description={
                <ProviderNotHereDescription
                  onClose={onClose}
                  clientHasAppointment={!!appointment}
                  isPastNoShowThreshold={isPastNoShowThreshold}
                  clientJoinedAfterWindowToReport={clientJoinedAfterWindowToReport}
                  recordProviderNoShowAndRedirect={recordProviderNoShowAndRedirect}
                  appointmentHasProviderNoShowReport={appointment?.hasProviderNoShowReport ?? false}
                />
              }
            />
          )}
          <HelpModalItem
            heading={t('telehealth-core:troubleshooting.iNeedHelpWithADifferentIssue')}
            description={
              <Translate
                i18nKey='telehealth-core:troubleshooting.seeOurTelehealthGuideForAdditionalHelp'
                components={{
                  1: (
                    <Link
                      external
                      className={cn(
                        'text-neutral-800 hover:text-neutral-900 rebrand:text-action-primary-textLink hover:rebrand:text-action-primary-textLinkHover',
                        {
                          'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                            isDarkTheme,
                        },
                      )}
                      href={CLIENT_TELEHEALTH_HELP_ARTICLE}
                    />
                  ),
                }}
              />
            }
          />
        </ol>
      </ModalBody>

      <ModalFooter
        actions={[
          {
            key: 'close-modal',
            actionContent: t('telehealth-core:troubleshooting.done'),
            onClick: onClose,
            use: 'secondary',
          },
        ]}
      >
        {supportText}
      </ModalFooter>
    </Modal>
  );
}
